import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ChecklistResponseTypes } from 'src/app/modules/shared/enums/checklists/checklistEnums';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { ObjectEventEmitters } from 'src/app/modules/shared/events/object.events';
import { PaginationManager } from 'src/app/modules/shared/managers/paginationManager';
import { ChecklistItemViewModel } from 'src/app/modules/shared/models/checklists/checklistItemViewModel';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { ChecklistItemService } from 'src/app/modules/shared/services/checklist-item-service';
import { ChecklistItemAbstraction } from 'src/app/modules/shared/services/abstractions/checklist-item.abstraction';
import { NormalUserChecklistService } from 'src/app/modules/shared/services/abstractions/normal-user-checklist.service';
import { ExternalUserChecklistService } from 'src/app/modules/shared/services/abstractions/external-user-checklist.service';
import { ExternalAuthService } from 'src/app/modules/external/services/external-auth.service';
import { RichTextEditorService } from 'src/app/modules/shared/services/richTextEditor.service';
import { T } from 'src/assets/i18n/translation-keys';

@Component({
    selector: 'app-checklist-modal',
    templateUrl: './checklist-modal.component.html',
    styleUrl: './checklist-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChecklistModalComponent implements OnInit, OnDestroy, AfterViewInit {
  public readonly T = T;
  public paginationId: string;
  public checklistItems: ChecklistItemViewModel[] = [];
  public canEdit: boolean = true;
  public title: string = '';
  public subscriptions = new Subscription();
  public responseTypes = ChecklistResponseTypes;
  public broadcastChecklistItemsUpdated = true;
  protected loadingChecklistItems = false;
  private focusTimeout?: ReturnType<typeof setTimeout>;
  private activeChecklistService: ChecklistItemAbstraction;
  private isExternalUser: boolean = false;
  private externalShareId: string | null = null;

  @Output() checklistAttachmentsChanged = new EventEmitter<void>();
  @ViewChild('checklistModal', { read: ElementRef }) modalElement?: ElementRef;
  @ViewChild('modalTitle', { read: ElementRef }) modalTitle?: ElementRef;

  constructor(
    public readonly bsModalRef: BsModalRef,
    private readonly paginationManager: PaginationManager,
    private readonly checklistItemService: ChecklistItemService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly translateService: TranslateService,
    private readonly alertService: AlertService,
    private readonly objectEventEmitters: ObjectEventEmitters,
    private richTextEditorService: RichTextEditorService,
    private readonly normalUserChecklistService: NormalUserChecklistService,
    private readonly externalUserChecklistService: ExternalUserChecklistService,
    private readonly externalAuthService: ExternalAuthService,
  ) {}

  ngOnInit(): void {
    const externalSession = this.externalAuthService.getCurrentSession();
    this.isExternalUser = externalSession !== null && this.externalAuthService.isAuthenticated();
    this.activeChecklistService = this.isExternalUser
      ? this.externalUserChecklistService
      : this.normalUserChecklistService;

    if (this.isExternalUser) {
      this.externalAuthService.currentSession$.pipe(take(1)).subscribe(session => {
        if (session) {
          this.externalShareId = session.externalShareId;
        }
      });
    }

    this.paginationId = this.paginationManager.generateID();
    this.setBackgroundInert(true);
  }

  ngAfterViewInit(): void {
    // Focus the modal title after it opens for accessibility
    this.focusTimeout = setTimeout(() => {
      if (this.modalTitle?.nativeElement) {
        this.modalTitle.nativeElement.focus();
      }
    }, 300);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.setBackgroundInert(false);

    if (this.focusTimeout) clearTimeout(this.focusTimeout);
  }

  private setBackgroundInert(inert: boolean): void {
    const appRoot = document.querySelector('app-root');
    if (appRoot) {
      if (inert) {
        appRoot.setAttribute('aria-hidden', 'true');
        appRoot.setAttribute('inert', '');
      } else {
        appRoot.removeAttribute('aria-hidden');
        appRoot.removeAttribute('inert');
      }
    }
  }

  public notifyAttachmentsChanged(): void {
    this.checklistAttachmentsChanged.emit();
  }

  public onAttachmentChanged(item: ChecklistItemViewModel): void {
    this.checklistAttachmentsChanged.emit();
  }

  public onClose(forceSave: boolean = false): void {
    if (forceSave && this.richTextEditorService.hasPendingChanges()) {
      const data = this.richTextEditorService.getCurrentEdit();
      if (data) {
        const editedItem = this.checklistItems.find(item => item.id === data.id);
        if (editedItem) {
          if (editedItem.responseType === ChecklistResponseTypes.Text_Response) {
            editedItem.response = data.content;
          }
          if (editedItem.responseType === this.responseTypes.Options_Set_2 || editedItem.responseType === this.responseTypes.Options_Set_1) {
            editedItem.textResponse = data.content;
          }
          this.updateItem(editedItem, true);
        }
      }
      return;
    }
    this.bsModalRef.hide();
  }

  public updateItem(item: ChecklistItemViewModel, closeModal: boolean = false): void {
    this.loadingChecklistItems = true;
    this.subscriptions.add(
      this.activeChecklistService.update(item, this.externalShareId)
        .subscribe({
          next: (res) => {
            if (this.broadcastChecklistItemsUpdated)
              this.objectEventEmitters.broadcastObjectUpdated(res.id, ObjectTypes.Checklist_Item, res);
            this.checklistItems = JSON.parse(JSON.stringify(this.checklistItems));

            this.loadingChecklistItems = false;
            this.richTextEditorService.clear();
            this.changeDetectorRef.detectChanges();
            void this.alertService.success(this.translateService.instant(T.common.changes_were_saved_successfully));
            if (closeModal) {
              this.bsModalRef.hide();
            }
          },
          error: (err) => {
            this.loadingChecklistItems = false;
            this.changeDetectorRef.detectChanges();
          }
        })
    )
  }
}
